import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { Notification } from 'iconsax-react';
import { isIos } from '../../lib/webPush';

// 코드 기반 하이퍼프레임 (MP4 금지 원칙) — 잠금화면 폰 목업에 실제 알림이
// 도착하는 장면을 무한 루프로 재생. 프리퍼미션 모달·Auth 페이지에서 공유.
// reduced-motion 사용자는 정적 스택.

// 루프로 도착하는 알림 시나리오 — 실제 발송될 메시지 그대로
export const DEMO_NOTIFICATIONS = [
    { title: '방금 프로필 방문이 감지되었습니다', body: '최근 1시간 조회 3회' },
    { title: '8개월 전 게시물에 새 좋아요가 추가되었습니다', body: '여기까지 스크롤해서 내려간 사람이 있어요' },
    { title: '프로필 조회가 평소보다 3.2배 빠르게 늘고 있어요', body: '지금 진행 중인 활동이에요' },
    { title: '어제 방문 기록 중 1건은 새벽 2시대에 발생했어요', body: '야간 활동 요약이 도착했어요' },
] as const;

const TIME_LABELS = ['지금', '2분 전', '9분 전'] as const;

/** 잠금화면 알림 도착 루프 — 크롭된 3D 폰 (컨테이너 높이 고정형) */
export const LockScreenHyperframe: React.FC<{ className?: string }> = ({ className = '' }) => {
    const reduce = useReducedMotion();
    // iOS Safari는 preserve-3d 회전 + 중첩 backdrop-filter 조합에서 내부 레이어의
    // 첫 페인트를 수 초 지연시킨다 (폰 윤곽만 먼저 보이는 증상). iOS는 평면(2D)으로 강등 —
    // 알림 도착 루프는 그대로 살아 있어 체감 손실 없음. 데스크톱은 3D 유지.
    const flat = reduce || isIos();
    const [feed, setFeed] = useState<{ id: number; msg: (typeof DEMO_NOTIFICATIONS)[number] }[]>([
        { id: 0, msg: DEMO_NOTIFICATIONS[0] },
    ]);

    useEffect(() => {
        if (reduce) {
            setFeed([0, 1, 2].map((i) => ({ id: i, msg: DEMO_NOTIFICATIONS[i % DEMO_NOTIFICATIONS.length] })));
            return;
        }
        let nextId = 1;
        const timer = window.setInterval(() => {
            setFeed((prev) => {
                const msg = DEMO_NOTIFICATIONS[nextId % DEMO_NOTIFICATIONS.length];
                const next = [{ id: nextId, msg }, ...prev].slice(0, 3);
                nextId += 1;
                return next;
            });
        }, 2600);
        return () => window.clearInterval(timer);
    }, [reduce]);

    return (
        <div className={`relative h-[300px] overflow-hidden rounded-2xl border border-[#262626] bg-[#0F0F0F] ${className}`}>
            {/* 배경 글로우 */}
            <div className="pointer-events-none absolute left-1/2 top-6 h-44 w-44 -translate-x-1/2 rounded-full bg-[#3182f6] opacity-[0.14] blur-[80px]" />

            {/* LIVE 배지 */}
            <div className="absolute right-3 top-3 z-10 flex items-center gap-1.5 rounded-full border border-[#00D87E]/25 bg-[#00D87E]/10 px-2.5 py-1 text-[10px] font-bold text-[#00D87E]">
                <motion.span
                    className="h-1.5 w-1.5 rounded-full bg-[#00D87E]"
                    animate={reduce ? undefined : { opacity: [1, 0.25, 1] }}
                    transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
                />
                실시간 미리보기
            </div>

            {/* 3D 폰 (하단은 컨테이너 밖으로 크롭) — iOS/reduced-motion은 평면 렌더 */}
            <div className="absolute inset-x-0 top-7 flex justify-center" style={flat ? undefined : { perspective: 1200 }}>
                <motion.div
                    className="relative"
                    style={{
                        width: 'min(60vw, 226px)',
                        aspectRatio: '360 / 740',
                        ...(flat ? {} : { transformStyle: 'preserve-3d' as const }),
                    }}
                    initial={flat ? undefined : { rotateY: -9, rotateX: 3 }}
                    animate={flat ? undefined : { rotateY: [-9, 9, -9], y: [0, -6, 0] }}
                    transition={flat ? undefined : { duration: 10, repeat: Infinity, ease: 'easeInOut' }}
                >
                    {/* 프레임 */}
                    <div
                        className="absolute inset-0 rounded-[36px] bg-[#0b0d10]"
                        style={{ boxShadow: '0 0 0 1.5px #20262e, 0 0 0 8px #0a0c0f, 0 40px 80px rgba(0,0,0,0.7), inset 0 0 0 1px rgba(255,255,255,0.04)' }}
                    />
                    <div className="absolute inset-0 rounded-[36px]" style={{ boxShadow: 'inset 2px 0 12px rgba(49,130,246,0.16), inset -2px 0 12px rgba(49,130,246,0.09)' }} />

                    {/* 잠금화면 */}
                    <div
                        className="absolute inset-[9px] overflow-hidden rounded-[28px] border border-[#161b21]"
                        style={{ background: 'radial-gradient(120% 90% at 50% 0%, #131a2b 0%, #0a0c11 58%, #07080b 100%)' }}
                    >
                        {/* 다이내믹 아일랜드 */}
                        <div className="absolute left-1/2 top-[10px] z-[5] h-[18px] w-[64px] -translate-x-1/2 rounded-full bg-black" />

                        {/* 시계 */}
                        <div className="absolute inset-x-0 top-[38px] text-center">
                            <div className="text-[10px] font-medium text-white/55">7월 14일 화요일</div>
                            <div className="mt-0.5 text-[40px] font-black leading-none tracking-tight text-white/95">21:47</div>
                        </div>

                        {/* 알림 스택 */}
                        <div className="absolute inset-x-[11px] top-[128px] space-y-[7px]">
                            <AnimatePresence mode="popLayout" initial={false}>
                                {feed.map((item, idx) => (
                                    <motion.div
                                        key={item.id}
                                        layout
                                        initial={{ opacity: 0, y: -26, scale: 0.95 }}
                                        animate={{ opacity: idx === 0 ? 1 : 0.55, y: 0, scale: 1 }}
                                        exit={{ opacity: 0, y: 14, scale: 0.97 }}
                                        transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                                        className={`rounded-[13px] border border-white/[0.06] p-2 ${
                                            flat ? 'bg-[#242b36]' : 'bg-white/[0.13] backdrop-blur-md'
                                        }`}
                                    >
                                        <div className="flex items-start gap-1.5">
                                            <div className="mt-[1px] flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[5px] bg-[#3182f6]">
                                                <Notification size={11} variant="Bold" className="text-white" style={{ color: '#ffffff' }} />
                                            </div>
                                            <div className="min-w-0">
                                                <div className="flex items-center justify-between gap-1">
                                                    <span className="text-[8.5px] font-bold text-white/90">소셜데이터랩</span>
                                                    <span className="shrink-0 text-[8px] text-white/45">{TIME_LABELS[idx] ?? '지금'}</span>
                                                </div>
                                                <p className="mt-[1px] truncate text-[9.5px] font-semibold leading-snug text-white/90">{item.msg.title}</p>
                                                <p className="truncate text-[8.5px] leading-snug text-white/60">{item.msg.body}</p>
                                            </div>
                                        </div>
                                    </motion.div>
                                ))}
                            </AnimatePresence>
                        </div>
                    </div>
                </motion.div>
            </div>

            {/* 하단 페이드 (폰 크롭 처리) */}
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-[#0F0F0F] to-transparent" />
        </div>
    );
};

/** 한 줄 라이브 알림 티커 — 모바일 등 좁은 영역용 컴팩트 버전 */
export const MiniNotificationTicker: React.FC<{ className?: string }> = ({ className = '' }) => {
    const reduce = useReducedMotion();
    const [idx, setIdx] = useState(0);

    useEffect(() => {
        if (reduce) return;
        const timer = window.setInterval(() => setIdx((i) => i + 1), 3200);
        return () => window.clearInterval(timer);
    }, [reduce]);

    const msg = DEMO_NOTIFICATIONS[idx % DEMO_NOTIFICATIONS.length];

    return (
        <div className={`relative h-[54px] overflow-hidden ${className}`}>
            <AnimatePresence mode="popLayout" initial={false}>
                <motion.div
                    key={idx}
                    initial={reduce ? false : { opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ type: 'spring', stiffness: 320, damping: 30 }}
                    className="absolute inset-0 flex items-start gap-2 rounded-xl border border-white/[0.06] bg-white/[0.06] p-2.5"
                >
                    <div className="mt-[1px] flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-md bg-[#3182f6]">
                        <Notification size={13} variant="Bold" className="text-white" style={{ color: '#ffffff' }} />
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-1">
                            <span className="text-[10px] font-bold text-white/90">소셜데이터랩</span>
                            <span className="flex items-center gap-1 text-[9px] text-[#00D87E]">
                                <motion.span
                                    className="h-1 w-1 rounded-full bg-[#00D87E]"
                                    animate={reduce ? undefined : { opacity: [1, 0.25, 1] }}
                                    transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
                                />
                                실시간
                            </span>
                        </div>
                        <p className="truncate text-[11px] font-medium leading-snug text-white/80">{msg.title}</p>
                    </div>
                </motion.div>
            </AnimatePresence>
        </div>
    );
};

export default LockScreenHyperframe;
